import { IntegrationEmail } from '../../../models/Email'
import React from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectEmailTypes, selectEmailTypesLoading } from '../../../store/emailHub/emailTypes'
import { selectCurrentUserEmailIntegration } from '../../../store/emailHub/emailUsers'
import { SelectDropdown, SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { useUpdateEmailMetadataMutation } from '../../../store/integrations/emailMetadata'
import { selectAccountId } from '../../../store/auth/authSlice'
import { updateEmailMetadata } from '../../../store/emailHub/emailMetadata'
import { EmailMetadata } from '../../../models/EmailMetadata'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

export interface EmailTypeSelectorProps {
  email: IntegrationEmail
  metadata: EmailMetadata | null
  disabled?: boolean
  onTypeChanged?: (emailTypeId: string | null) => void
}

export const EmailTypeSelector: React.FC<EmailTypeSelectorProps> = ({ email, metadata, disabled = false, onTypeChanged }) => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)

  const emailTypes = useAppSelector(selectEmailTypes)
  const emailTypesLoading = useAppSelector(selectEmailTypesLoading)
  const userEmailIntegration = useAppSelector(selectCurrentUserEmailIntegration)

  const [triggerUpdateEmailMetadata, { isLoading: emailMetadataUpdateLoading }] = useUpdateEmailMetadataMutation()

  const emailTypeIdForThread = metadata?.typeProcessing.emailTypeId ?? null

  const emailTypeOptions = emailTypes.map((emailType) => {
    return {
      id: emailType.id,
      label: emailType.name,
    }
  })

  const _handleOptionSelected = async (option: SelectOption) => {
    await updateEmailTypeId(option.id)
  }

  const handleOptionSelected = tracked(
    _handleOptionSelected,
    { eventName: TrackEvent.EMAIL_RESPONSE_EMAIL_TYPE_CHANGED },
    {
      eventDetailsExtractor: (option: SelectOption) => {
        return {
          threadId: email.threadId,
          emailId: email.externalId,
          newEmailTypeId: option.id,
        }
      },
    },
  )

  const handleDeselect = async () => {
    await updateEmailTypeId(null)
  }

  const updateEmailTypeId = async (emailTypeId: string | null) => {
    if (!email || !userEmailIntegration) {
      return
    }

    try {
      const emailMetadata = await triggerUpdateEmailMetadata({
        accountId,
        emailConnector: userEmailIntegration.serviceId,
        externalId: email.externalId,
        payload: {
          threadId: email.threadId,
          senderEmail: email.sender.email,
          typeProcessing: {
            emailTypeId,
          },
        },
        removeEmptyValues: false,
      }).unwrap()
      dispatch(updateEmailMetadata(emailMetadata))
      onTypeChanged && onTypeChanged(emailTypeId)
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <SelectDropdown
      className='py-1 zocks-px-0.75'
      placeholder='Select type'
      options={emailTypeOptions}
      initialSelectedOption={emailTypeOptions.find((option) => option.id === emailTypeIdForThread) ?? null}
      onOptionSelected={handleOptionSelected}
      showDeselectOption
      deselectOptionLabel='Clear'
      onDeselect={handleDeselect}
      disabled={disabled || emailTypesLoading || emailMetadataUpdateLoading}
    />
  )
}
